/// <reference types="vite/client" />

/**
 * Failure policy for the resource kit (#1799).
 *
 * The reference directive above is deliberate. `core/tsconfig.json` sets
 * `types: []` so nothing is pulled in automatically; this file names its one
 * bundler dependency explicitly, because the one decision below reads
 * `import.meta.env`. It does not let core reach into the host app, which is
 * what the boundary project actually gates.
 *
 * It is the only such directive in this package, and that is a property worth
 * keeping: a reference directive's reach is the whole PROGRAM, so a second one
 * anywhere under `core/` — `__tests__/` included, since the tsconfig compiles
 * it with the sources — widens what every file here can see. `__tests__/
 * fail-loud.test.ts` briefly named `types="node"` for a child-process oracle,
 * and while it did, `process.env.HOME + __dirname` appended to THIS file
 * type-checked clean (measured 2026-09-07): the tsconfig's `types: []` had
 * stopped biting. That oracle now lives in
 * `src/__tests__/architecture/resourceKitPackagesHostFree.test.ts`, whose tree
 * already types node.
 *
 * It is a TYPE dependency and only that: nothing here assumes the object
 * EXISTS at runtime. Every read is optional-chained, so a host whose bundler
 * injects no `import.meta.env` reads `undefined` instead of throwing (#2189).
 * Measured 2026-09-07: plain Node ESM imports THIS MODULE by path and
 * `failLoud` logs and RETURNS instead of throwing;
 * `src/__tests__/architecture/resourceKitPackagesHostFree.test.ts` runs
 * exactly that as an oracle. What does NOT follow is that the whole package
 * loads there — `import('@resource-kit/core')` still fails under Node ESM,
 * which requires explicit extensions on relative specifiers while this
 * package's are extensionless. Bundlers resolve those. The guarantee this
 * paragraph makes is about the env reads, not about Node's resolver.
 *
 * A misconfigured definition — an unregistered widget, a widget pointed at a
 * type it cannot render, a predicate declared on a surface that has no context
 * — is an authoring bug. Silently degrading it produces a page that looks fine
 * and is wrong, which is the failure mode this kit exists to remove. So:
 *
 *   - dev & test: `throw`, immediately, at the point of the mistake;
 *   - prod, and any host that names no mode: `console.error`, and let the
 *     caller fall back to a documented
 *     default, because a blank admin console helps nobody at 2am.
 *
 * There is no third mode. A caller that has nothing to degrade to must call
 * `failHard` instead, which throws everywhere.
 */
export class ResourceKitError extends Error {
  constructor(message: string) {
    super(`[resource-kit] ${message}`);
    this.name = 'ResourceKitError';
  }
}

/**
 * True in development and under test — the environments where an authoring
 * mistake must stop the world rather than be logged and forgotten.
 *
 * Each read takes one KEY of `import.meta.env` through an optional chain, and
 * both halves of that shape are load-bearing:
 *
 *   - the optional chain is what makes the package portable. The object exists
 *     only where a bundler injects one, and under a host that injects NOTHING
 *     a plain member access on it throws — on every call, for a host that has
 *     done nothing wrong. "Not Vite" is not that condition and would be the
 *     wrong one to write: measured 2026-09-07, `bun -e 'console.log(
 *     import.meta.env === process.env)'` prints `true`, so Bun injects an
 *     object here as well. Measured the same day, `?.` costs a Vite host
 *     nothing: `vp build` folds both reads to the same literal and emits a
 *     chunk with the same content hash as the unguarded form.
 *   - per-key rather than whole-object, so what a bundler substitutes stays
 *     visible at the read site and `vi.stubEnv` keeps driving it.
 *
 * `src/__tests__/architecture/resourceKitPackagesHostFree.test.ts` holds both,
 * plus the rule that this is the only file under `packages/` allowed to read
 * the object at all.
 *
 * This paragraph used to add that a whole-object read is a snapshot taken at
 * transform time that `vi.stubEnv` never sees, and that hoisting `env` into a
 * local pins the decision to dev/test forever. That was not reproducible: on
 * 2026-09-07 both an in-function and a module-scope hoist left
 * `widgets.test.ts` green at 15/15 under vite-plus-test 0.1.16, whose
 * `import.meta.env` is a live object the stub mutates in place. The per-key
 * form is worth keeping and is now held by the guard rather than by that
 * claim.
 */
function isFailFastEnvironment(): boolean {
  return Boolean(import.meta.env?.DEV) || import.meta.env?.MODE === 'test';
}

/**
 * Report an authoring mistake. Throws in dev/test; in prod logs and returns so
 * the caller can apply its documented fallback.
 *
 * THE ONLY MODE-DEPENDENT DECISION IN THE KIT IS `isFailFastEnvironment()`
 * ABOVE. This file briefly exported a second one — a "is this a production
 * build" predicate that `ResourcePage` asked before running its whitelist
 * assertions — so the kit had both a "should I CHECK" question and a "should I
 * THROW" question and had to answer each for a host that says nothing. Two
 * answers for one unknown is one too many, and the CHECK side picked the
 * crashing one: those assertions were raw `throw new Error`, so a host
 * injecting no env ran them AND threw, turning a misconfigured resource into an
 * error boundary on every render. That contradicted this file's own rule that
 * silence must mean "log, do not crash" (#2189).
 *
 * That predicate is gone rather than re-defaulted. The assertions now run
 * UNCONDITIONALLY and report through this function, so the mode decides only
 * how the report is delivered — throw in dev/test, `console.error` in a
 * production build and in a host that named no mode. Nothing is lost by
 * checking everywhere: a quick filter aimed outside the backend whitelist is
 * already surfaced once it is applied, by `droppedFilterFields`
 * (`packages/resource-kit-controllers/list/whitelist.ts::droppedFilterFields`),
 * which feeds a
 * `role="alert"` banner (`packages/resource-kit-ui/react/list/SearchView.tsx::SearchView`,
 * `data-testid="filters-dropped-warning"`) and a `console.warn`
 * (`packages/resource-kit-controllers/react/listController.ts::useUrlOwnedList`).
 * What the render-time
 * assertion adds is WHEN and WHAT: it fires before anyone interacts and names
 * the misconfigured declaration.
 */
export function failLoud(message: string): void {
  if (isFailFastEnvironment()) throw new ResourceKitError(message);
  console.error(new ResourceKitError(message).message);
}

/** Report an authoring mistake that has no fallback. Throws everywhere. */
export function failHard(message: string): never {
  throw new ResourceKitError(message);
}
